import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { siteSocialsService } from 'components/api/siteSocials';
import {
  SiteSocial,
  SiteSocialsResponse,
  SiteSocialParams,
  CreateSiteSocialRequest,
} from 'types/siteSocial';

interface SiteSocialsState {
  data: SiteSocial[];
  isLoading: boolean;
  isCreating: boolean;
  error: string | null;
  params: SiteSocialParams;
}

const initialState: SiteSocialsState = {
  data: [],
  isLoading: false,
  isCreating: false,
  error: null,
  params: {
    siteId: '',
  },
};

export const fetchSiteSocials = createAsyncThunk(
  'siteSocials/fetchSiteSocials',
  async (params: SiteSocialParams, { rejectWithValue }) => {
    try {
      const response = await siteSocialsService.getSiteSocials(params);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to fetch social links',
      );
    }
  },
);

export const createSiteSocial = createAsyncThunk(
  'siteSocials/createSiteSocial',
  async (data: CreateSiteSocialRequest, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await siteSocialsService.createSiteSocial(data);
      const { siteSocials } = getState() as { siteSocials: SiteSocialsState };
      dispatch(fetchSiteSocials(siteSocials.params));
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to create social link',
      );
    }
  },
);

export const updateSiteSocial = createAsyncThunk(
  'siteSocials/updateSiteSocial',
  async (
    { id, data, image }: { id: string; data: Partial<CreateSiteSocialRequest>; image?: File },
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      if (Object.keys(data).length > 0) {
        await siteSocialsService.updateSiteSocial(id, data);
      }
      if (image) {
        await siteSocialsService.updateSocialImage(id, image);
      }
      const { siteSocials } = getState() as { siteSocials: SiteSocialsState };
      dispatch(fetchSiteSocials(siteSocials.params));
      return { id };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to update social link',
      );
    }
  },
);

export const deleteSiteSocial = createAsyncThunk(
  'siteSocials/deleteSiteSocial',
  async (id: string, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await siteSocialsService.deleteSiteSocial(id);
      const { siteSocials } = getState() as { siteSocials: SiteSocialsState };
      dispatch(fetchSiteSocials(siteSocials.params));
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to delete social link',
      );
    }
  },
);

const siteSocialsSlice = createSlice({
  name: 'siteSocials',
  initialState,
  reducers: {
    setParams: (state, action: PayloadAction<Partial<SiteSocialParams>>) => {
      state.params = { ...state.params, ...action.payload };
    },
    clearData: (state) => {
      state.data = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSiteSocials.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSiteSocials.fulfilled, (state, action: PayloadAction<SiteSocialsResponse>) => {
        state.isLoading = false;
        state.data = action.payload.data || [];
      })
      .addCase(fetchSiteSocials.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createSiteSocial.pending, (state) => {
        state.isCreating = true;
      })
      .addCase(createSiteSocial.fulfilled, (state) => {
        state.isCreating = false;
      })
      .addCase(createSiteSocial.rejected, (state) => {
        state.isCreating = false;
      });
  },
});

export const { setParams, clearData } = siteSocialsSlice.actions;
export default siteSocialsSlice.reducer;
